import { Injectable } from "@angular/core";
import { Rate } from "../model/Rate";
import { RateCache, CURVE_SPT, CURVE_FWD, CURVE_NDF } from "./RateCache";
import { RateSubscription } from "../model/RateSubscription";
import { MarketDataEventTransformer } from "../event/MarketDataEventTransformer";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { IRateService, CHANNEL_RATE } from "../IRateService";
import { Subject } from "rxjs";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import {
  MarketDataEventImpl,
  Product,
  Tenor,
  MarketRateImpl,
  MarketRate,
} from "shared/cubiq-model-multi-asset";

export const RATE_PROCESS_INTERVAL: number = 250;
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { CcyPairPromiseLookupService } from "shared/cubiq-services/lookup/model/impl/CcyPairPromiseLookupService";
import { NotificationService } from "../../lookup/notification/impl/NotificationService";
import { BFXNotificationType } from "../../model/core/notifications/BFXNotificationType";
import { BFXNotificationSubType } from "../../model/core/notifications/BFXNotificationSubType";
import { BFXNotificationEventType } from "../../model/core/notifications/BFXNotificationEventType";

@Injectable()
export class RatePrimaryService implements IRateService {
  protected preProcessedRates: Map<string, Rate> = new Map();
  protected rateProcessingTicker: any = null;
  protected broker: ServiceMessageBroker;
  protected ports: Array<MessagePort> = new Array<MessagePort>();
  private ratesStreaming: boolean = true;
  private _rateEventSubject: Subject<Array<Rate>> = new Subject<Array<Rate>>();
  private pausedRatesMap: any = {};
  protected rateStaleMap: Map<string, { instrument: string; stale: boolean }> = new Map();
  private isCurrencyPairServicePrimed: boolean = false;
  private rateProcessingBusy = false;

  constructor(
    protected rateCache: RateCache,
    private worker: CubiqWorker,
    private rateTransformer: MarketDataEventTransformer,
    private currencyPairService: CcyPairPromiseLookupService,
    private notificationService: NotificationService,
    protected factory: ServiceMessageBrokerFactory,
  ) {
    this.initialize();
  }

  protected initialize(): void {
    this.broker = this.factory.createMessageBroker(CHANNEL_RATE, true, true);
    this.broker.registerMethod("initPort", [PRIMITIVE], this.initPort.bind(this), null, false);
    this.broker.registerMethod("getSpotRate", [PRIMITIVE], this.getSpotRate.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "getSpotRates",
      [PRIMITIVE],
      this.getSpotRates.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getRate",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.getRate.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("getRates", [PRIMITIVE], this.getRates.bind(this), PRIMITIVE);
    this.broker.registerMethod("pauseStream", [], this.pauseStream.bind(this), PRIMITIVE);
    this.broker.registerMethod("resumeStream", [], this.resumeStream.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "isStreamingPaused",
      [],
      this.isStreamingPaused.bind(this),
      PRIMITIVE,
    );
    this.worker
      .getBroker()
      .registerEventChannelListener(
        MarketDataEventImpl.name,
        this.processMarketDataEvent.bind(this),
      );

    this.worker.isAuth().then(() => {
      this.currencyPairService.isPrimed().then(() => {
        // this.rateProcessingTicker = setInterval(this.processQueuedRates.bind(this), RATE_PROCESS_INTERVAL);
        this.isCurrencyPairServicePrimed = true; //NEEDS REVIEW
      });
    });
  }

  public getRateEventSubject(): Subject<Array<Rate>> {
    return this._rateEventSubject;
  }

  public initPort(port: MessagePort): void {
    this.ports.push(port);
  }

  protected processMarketDataEvent(event: MarketDataEventImpl): void {
    if (event === null) return;

    let rates: Rate[] = this.rateTransformer.transformEvent(event);
    rates.forEach((rate: Rate) => this.preProcessedRates.set(rate.key, rate));

    this.processQueuedRates();
  }

  private processQueuedRates(): void {
    if (this.isCurrencyPairServicePrimed && !this.rateProcessingBusy) {
      //NEEDS REVIEW
      this.rateProcessingBusy = true;
      let allRates: Rate[] = Array.from(this.preProcessedRates.values());
      let clearNotifs: Array<string> = [];
      this.preProcessedRates.clear();

      if (this.ratesStreaming && allRates.length > 0) {
        this.rateCache.addRates(allRates);
        this._rateEventSubject.next(allRates);
        if (this.notificationService.shouldNotify(BFXNotificationType.STREAM_OFF)) {
          allRates.forEach((rate: Rate) => {
            if (rate.tenor === Tenor.SPOT) {
              if (!rate.tradable) {
                let key: string = rate.baseCurrency + rate.nonBaseCurrency;
                if (!this.rateStaleMap.get(key)) {
                  this.rateStaleMap.set(key, { instrument: key, stale: true });
                  this.notificationService.fireNotificationEvent(
                    this.notificationService.rateDataToNotification(
                      rate,
                      BFXNotificationType.STREAM_OFF,
                      BFXNotificationSubType.STREAM_OFF_RATE_STALE,
                    ),
                    BFXNotificationEventType.ORDER_UPDATE_EVENT,
                  );
                }
              } else {
                let key: string = rate.baseCurrency + rate.nonBaseCurrency;
                if (this.rateStaleMap.get(key)) {
                  this.rateStaleMap.delete(key);
                  clearNotifs.push(key);
                }
              }
            }
          });
          if (clearNotifs.length > 0) {
            this.notificationService.fireNotificationClearEvent(
              this.notificationService.dataToClearEvent(clearNotifs),
              BFXNotificationEventType.ORDER_UPDATE_EVENT,
            );
          }
        }
      } else if (allRates != null && allRates.length > 0) {
        allRates.forEach((rate: Rate) => (this.pausedRatesMap[rate.key] = rate));
      }
      setTimeout(this.checkRateQueue.bind(this), RATE_PROCESS_INTERVAL); //NEEDS REVIEW
    }
  }

  private checkRateQueue() {
    //NEEDS REVIEW
    this.rateProcessingBusy = false;
    if (this.preProcessedRates.size > 0) this.processQueuedRates();
  }

  public getSpotRate(productCode: string): Promise<Rate> {
    return new Promise<Rate>((res, rej) => {
      res(this.rateCache.getRate(productCode, CURVE_SPT, Tenor.SPOT, null));
    });
  }

  public getSpotRates(productCodes: Array<string>): Promise<Array<Rate>> {
    return new Promise<Array<Rate>>((res, rej) => {
      let rates: Array<Rate> = new Array<Rate>();
      for (let i: number = 0; i < productCodes.length; i++) {
        let rate: Rate = this.rateCache.getRate(productCodes[i]);
        if (rate) {
          rates.push(rate);
        }
      }

      return rates;
    });
  }

  public getRate(
    productCode: string,
    product: Product = Product.SPT,
    tenor: Tenor,
    valueDate: Date = null,
  ): Promise<Rate> {
    let curveId;

    switch (product) {
      case Product.SPT:
        curveId = CURVE_SPT;
        break;
      case Product.FWD:
        curveId = CURVE_FWD;
        break;
      case Product.NDF:
        curveId = CURVE_NDF;
        break;
      default:
        throw new Error(
          `${this.constructor.name}.getSubscription() unsupported product ${product}`,
        );
    }

    return new Promise<Rate>((res, rej) => {
      res(this.rateCache.getRate(productCode, curveId, tenor, valueDate));
    });
  }

  public getRates(rateSubscriptions: Array<RateSubscription>): Promise<Array<Rate>> {
    return new Promise<Array<Rate>>((res, rej) => {
      let rates: Array<Rate> = new Array<Rate>();
      for (let i: number = 0; i < rateSubscriptions.length; i++) {
        let subs: RateSubscription = rateSubscriptions[i];
        let rate: Rate = this.rateCache.getRate(
          subs.assetCode,
          subs.product,
          subs.tenor,
          subs.valueDate,
        );
        if (rate) {
          rates.push(rate);
        }
      }

      return rates;
    });
  }

  public pauseStream(): Promise<void> {
    return new Promise<void>((res) => {
      this.ratesStreaming = false;
      res();
    });
  }

  public resumeStream(): Promise<void> {
    return new Promise<void>((res) => {
      this.flushPausedRates();
      this.ratesStreaming = true;
      res();
    });
  }

  public isStreamingPaused(): Promise<boolean> {
    return new Promise<boolean>((res) => {
      res(!this.ratesStreaming);
    });
  }

  protected flushPausedRates(): void {
    let rates: Array<Rate> = Object.keys(this.pausedRatesMap).map(
      (key: string) => this.pausedRatesMap[key],
    );
    this.rateCache.addRates(rates);
    this._rateEventSubject.next(rates);
    this.pausedRatesMap = {};
  }
}
